// src/web/features/materials/pages/MaterialsListPage.tsx
import React, { useMemo, useCallback } from 'react';
import { Card, CardTitle, CardContent, CardDescription } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Alert, AlertTitle, AlertDescription } from '@voilajsx/uikit/alert';
import { useMaterials } from '../hooks/useMaterials';
import { FilterBar } from '../components/FilterBar';
import { useAuth } from '../../auth';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Upload,
  FileText,
  Download,
  Eye,
  GraduationCap,
  AlertCircle,
  Plus,
  Edit,
  Trash2,
  Calendar,
  Lock,
  Unlock,
  Phone
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../../../shared/components';
import { config } from '../../auth/config';

// Subject color mappings - defined outside component to avoid recreation
const SUBJECT_COLORS: { [key: string]: string } = {
  'Mathematics': 'bg-gradient-to-br from-blue-500 to-blue-600',
  'Physics': 'bg-gradient-to-br from-purple-500 to-purple-600',
  'Chemistry': 'bg-gradient-to-br from-green-500 to-green-600',
  'Biology': 'bg-gradient-to-br from-emerald-500 to-emerald-600',
  'English': 'bg-gradient-to-br from-red-500 to-red-600',
  'Hindi': 'bg-gradient-to-br from-orange-500 to-orange-600',
  'Social Science': 'bg-gradient-to-br from-amber-500 to-amber-600',
  'Computer Science': 'bg-gradient-to-br from-indigo-500 to-indigo-600',
  'Accountancy': 'bg-gradient-to-br from-teal-500 to-teal-600',
  'Business Studies': 'bg-gradient-to-br from-cyan-500 to-cyan-600',
  'Economics': 'bg-gradient-to-br from-rose-500 to-rose-600',
  'Geography': 'bg-gradient-to-br from-lime-500 to-lime-600',
  'History': 'bg-gradient-to-br from-yellow-500 to-yellow-600',
  'Political Science': 'bg-gradient-to-br from-pink-500 to-pink-600',
  'Environmental Science': 'bg-gradient-to-br from-green-400 to-green-500',
  'General Knowledge': 'bg-gradient-to-br from-violet-500 to-violet-600'
};

interface Material {
  id: number;
  title: string;
  slug?: string;
  description?: string;
  subject: string;
  classSection: string;
  createdAt: string;
  fileUrl: string;
  fileName?: string;
  accessType?: 'free' | 'restricted';
}

const MaterialsListPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { materials, loading, error, filters, setFilters, deleteMaterial } = useMaterials();

  // Handle view - navigate to embedded viewer using slug or ID
  const handleView = useCallback((identifier: string | number, accessType?: string) => {
    // If restricted and not logged in, redirect to contact page
    if (accessType === 'restricted' && !user) {
      navigate('/contact');
      return;
    }
    // Navigate to the embedded viewer page using slug or ID
    navigate(`/materials/view/${identifier}`);
  }, [user, navigate]);

  // Handle download with signed URL - works for logged-in users
  const handleDownload = useCallback(async (identifier: string | number, accessType?: string) => {
    // If restricted and not logged in, redirect to contact page
    if (accessType === 'restricted' && !user) {
      navigate('/contact');
      return;
    }

    try {
      // Optional auth - add token if available
      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${config.api.baseUrl}/api/materials/${identifier}/download`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));

        // Handle login required response - redirect to contact for premium materials
        if (errorData.loginRequired || errorData.error === 'login_required') {
          navigate('/contact');
          return;
        }

        alert(errorData.error || 'Failed to access material');
        return;
      }

      const data = await response.json();

      if (data.success && data.fileUrl) {
        // Direct download using fetch and blob (more reliable)
        try {
          const fileResponse = await fetch(data.fileUrl);

          if (fileResponse.ok) {
            const blob = await fileResponse.blob();
            const downloadUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = data.fileName || 'download';
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the object URL
            window.URL.revokeObjectURL(downloadUrl);
          } else {
            throw new Error('Failed to fetch file');
          }
        } catch {
          // Fallback: Use direct link method
          const link = document.createElement('a');
          link.href = data.fileUrl;
          link.download = data.fileName || 'download';
          link.target = '_blank';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        alert('Failed to get download URL');
      }

    } catch {
      alert('Failed to download material. Please try again.');
    }
  }, [user, navigate]);

  const handleDelete = useCallback(async (id: number, title: string) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await deleteMaterial(id);
      } catch {
        alert('Failed to delete material. Please try again.');
      }
    }
  }, [deleteMaterial]);

  // Check if user is admin (for admin features)
  const isAdmin = user?.role === 'admin';

  // Check if user is logged in
  const isLoggedIn = !!user;

  // Memoize stats calculations for admin view
  const stats = useMemo(() => ({
    totalMaterials: materials.length,
    uniqueClasses: new Set(materials.map(m => m.classSection)).size,
    uniqueSubjects: new Set(materials.map(m => m.subject)).size
  }), [materials]);

  // Card component for materials grid view
  const MaterialCard: React.FC<{ material: Material }> = ({ material }) => {
    const isPremium = material.accessType === 'restricted';
    // For non-logged-in users, restricted materials require login
    const needsLogin = isPremium && !isLoggedIn;

    const getFileName = (material: Material) => {
      if (material.fileName) {
        return material.fileName;
      }

      // Handle file keys (materials/timestamp-filename.ext)
      if (material.fileUrl && material.fileUrl.includes('/')) {
        const parts = material.fileUrl.split('/');
        const fileName = parts[parts.length - 1];
        // Remove timestamp prefix if present
        const cleanFileName = fileName.replace(/^\d+-/, '');
        return cleanFileName || 'Download File';
      }

      return material.fileUrl || 'Download File';
    };

    return (
      <Card className={`hover:shadow-lg transition-all duration-300 border-t-4 overflow-hidden ${
        isPremium ? 'border-t-amber-500' : 'border-t-green-500'
      }`}>
        <div className={`${SUBJECT_COLORS[material.subject] || 'bg-gradient-to-br from-gray-500 to-gray-600'} px-4 py-3`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-white" />
              <span className="font-medium text-white text-sm">{material.subject}</span>
            </div>
            {/* Always show access type badge */}
            {isPremium ? (
              <div className="flex items-center gap-1 bg-amber-500/80 px-2 py-1 rounded text-white text-xs font-medium">
                <Lock className="h-3 w-3" />
                <span>Premium</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 bg-green-500/80 px-2 py-1 rounded text-white text-xs font-medium">
                <Unlock className="h-3 w-3" />
                <span>Free</span>
              </div>
            )}
          </div>
        </div>

        <CardContent className="p-4">
          <div className="space-y-3">
            <div>
              <h3 className="font-semibold text-gray-900 text-lg leading-tight mb-1">
                {material.title}
              </h3>
              {material.description && (
                <p className="text-sm text-gray-600 line-clamp-2">
                  {material.description}
                </p>
              )}
            </div>

            <div className="flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <GraduationCap className="h-3 w-3" />
                <span>{material.classSection}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>{new Date(material.createdAt).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="flex items-center gap-1 text-xs text-gray-500">
              <FileText className="h-3 w-3" />
              <span className="truncate">{getFileName(material)}</span>
            </div>

            {/* Access type notice - only show for premium materials when not logged in */}
            {isPremium && !isLoggedIn && (
              <div className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                <Lock className="h-3 w-3" />
                <span>Contact our counsellors to access this material</span>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2 pt-2">
              {needsLogin ? (
                <Link to="/contact" className="flex-1">
                  <Button
                    size="sm"
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  >
                    <Phone className="mr-1 h-3 w-3" />
                    Contact Counsellor
                  </Button>
                </Link>
              ) : (
                <>
                  <Button
                    onClick={() => handleView(material.slug || material.id, material.accessType)}
                    size="sm"
                    variant="outline"
                    className={`${isLoggedIn ? 'flex-1' : 'w-full'} border-blue-200 text-blue-600 hover:bg-blue-50`}
                  >
                    <Eye className="mr-1 h-3 w-3" />
                    View
                  </Button>
                  {/* Download button only visible to logged-in users */}
                  {isLoggedIn && (
                    <Button
                      onClick={() => handleDownload(material.slug || material.id, material.accessType)}
                      size="sm"
                      className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    >
                      <Download className="mr-1 h-3 w-3" />
                      Download
                    </Button>
                  )}
                </>
              )}
            </div>

            {/* Admin tools - only visible to admin */}
            {isAdmin && (
              <div className="flex gap-2 pt-2 border-t border-gray-100 mt-2">
                <Button
                  onClick={() => navigate(`/materials/admin/edit/${material.id}`)}
                  size="sm"
                  variant="outline"
                  className="flex-1 border-orange-200 text-orange-600 hover:bg-orange-50"
                >
                  <Edit className="mr-1 h-3 w-3" />
                  Edit
                </Button>
                <Button
                  onClick={() => handleDelete(material.id, material.title)}
                  size="sm"
                  variant="outline"
                  className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="mr-1 h-3 w-3" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <SEO
        title="Study Materials - Tuition Point"
        description="Access your educational materials and resources"
      />
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                  <BookOpen className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-white">
                    {isAdmin ? 'Manage Materials' : 'Study Materials'}
                  </h1>
                  <p className="mt-1 text-green-100 text-sm">
                    {isAdmin
                      ? 'Upload and manage educational content for students'
                      : 'Browse and access educational study materials'}
                  </p>
                </div>
              </div>
              
              {isAdmin && (
                <Button
                  onClick={() => navigate('/materials/admin/upload')}
                  className="bg-white text-green-600 hover:bg-green-50 shadow-md"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Upload New Material
                </Button>
              )}
            </div>
          </div>

          {/* Materials Section */}
          <Card className="border-t-4 border-t-green-600 shadow-md overflow-hidden p-0">
            <div className="bg-gradient-to-r from-green-100 to-emerald-100 px-6 py-5">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FileText className="h-5 w-5 text-green-600" />
                </div>
                Available Materials
              </CardTitle>
              <CardDescription className="mt-1.5">
                {isAdmin
                  ? 'All uploaded materials across different classes'
                  : 'Browse materials by class and subject'}
              </CardDescription>
            </div>
            
            <CardContent className="p-6">
              {/* Filter bar available for all users */}
              <div className="mb-6">
                <FilterBar filters={filters} setFilters={setFilters} />
              </div>

              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Card Grid View - same for all users */}
              <div>
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1,2,3,4,5,6].map((i) => (
                      <div key={i} className="animate-pulse">
                        <div className="bg-gray-200 rounded-lg h-48"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {materials.map((material) => (
                      <MaterialCard key={material.id} material={material} />
                    ))}
                  </div>
                )}
              </div>

              {!loading && materials.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-700 mb-2">
                    No materials available
                  </h3>
                  <p className="text-gray-500 mb-6">
                    {isAdmin
                      ? 'Start by uploading your first educational material.'
                      : 'No study materials match your current filters. Try adjusting the class or subject filters.'}
                  </p>
                  {isAdmin && (
                    <Button
                      onClick={() => navigate('/materials/admin/upload')}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Upload First Material
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats Card - Only for Admin */}
          {isAdmin && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-t-4 border-t-blue-500 shadow-md overflow-hidden p-0">
                <div className="bg-gradient-to-r from-blue-100 to-sky-100 px-6 py-5">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="h-4 w-4 text-blue-600" />
                    </div>
                    Total Materials
                  </CardTitle>
                </div>
                <CardContent className="p-6">
                  <p className="text-2xl font-bold text-blue-600">{stats.totalMaterials}</p>
                  <p className="text-sm text-gray-600">Uploaded files</p>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-t-purple-500 shadow-md overflow-hidden p-0">
                <div className="bg-gradient-to-r from-purple-100 to-violet-100 px-6 py-5">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <GraduationCap className="h-4 w-4 text-purple-600" />
                    </div>
                    Classes Covered
                  </CardTitle>
                </div>
                <CardContent className="p-6">
                  <p className="text-2xl font-bold text-purple-600">{stats.uniqueClasses}</p>
                  <p className="text-sm text-gray-600">Different classes</p>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-t-orange-500 shadow-md overflow-hidden p-0">
                <div className="bg-gradient-to-r from-orange-100 to-amber-100 px-6 py-5">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <BookOpen className="h-4 w-4 text-orange-600" />
                    </div>
                    Subjects
                  </CardTitle>
                </div>
                <CardContent className="p-6">
                  <p className="text-2xl font-bold text-orange-600">{stats.uniqueSubjects}</p>
                  <p className="text-sm text-gray-600">Different subjects</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MaterialsListPage;
